import { serializeError } from "serialize-error";

import type * as amplitude from "./amplitude";

import { type AbstractUser } from "../../user";

import {
  createEventLogger,
  type LogEvent,
} from "./amplitude/create-event-logger";
import { LoggerTransports } from "./constants";
import {
  type LoggerContext,
  type ParsedLoggerContext,
  prettifyContext,
  type LoggerLocation,
  type LoggerParsedLocation,
  parseLocation,
  locationIsParsed,
} from "./context";
import { type LoggerConfig } from "./logger-config";
import { type LoggerOptions, type StaticLoggerOptions } from "./options";

type LogMethodOptions = {
  /**
   * Determines whether or not context should be automatically injected into the log data.
   *
   * Context can be automatically injected into the log data based on configuration attributes on
   * the {@link Logger} instance or the associated {@link LoggerConfig} instance it is initialized
   * with.
   *
   * Default: true
   */
  readonly autoInjectContext?: boolean;
};

type ErrorMethodOptions<
  Arg extends Error | string | C,
  C extends LoggerContext<U>,
  U extends AbstractUser,
> = LogMethodOptions & {
  /**
   * Defines whether or not the information should be passed to the 'notify' method, to perform
   * a notification process with BugSnag.
   *
   * Default: false
   */
  readonly notify?: Arg extends Error | string ? boolean : never;
};

type ContextTransformerReturn<
  C extends LoggerContext<U>,
  O extends LogMethodOptions,
  U extends AbstractUser,
> = O extends { autoInjectContext: false }
  ? C | null
  : ParsedLoggerContext<U, C>;

/**
 * The core class for logging in the application.  This class is responsible for wrapping a standard
 * logger, {@link pino}, and implementing additional methods and behavior specific to this
 * application around it.  These additional implementations include:
 *
 * 1. Amplitude Event Logging
 *    The ability to log events to Amplitude is encapsulated in this {@link Logger} via the 'event'
 *    method.
 *
 * 2. Bugsnag Error Notification
 *    The ability to emit errors to BugSnag is encapsulated in this {@link Logger} via the 'notify'
 *    method.
 *
 * 3. Internal API Request Errors
 *    The ability to log information about an HTTP error that occurs when communicating with the
 *    internal API in a systematic, standardized way is encapsulated in this {@link Logger}'s
 *    'internalRequestError' method.
 *
 * Note: This {@link Logger} class should not be used to directly instantiate new {@link Logger}
 * instances, but rather, new {@link Logger} instances should be instantiated via the 'createLogger'
 * method on the {@link LoggerConfig} instance specific to that sub-project.
 *
 * Support
 * -------
 * This {@link Logger} class is separately configurable for 'nb-frontend' and 'nb-react-native'
 * sub-projects currently, but will eventually be configurable for 'nb-api' as well.
 *
 * Configuration
 * -------------
 * The {@link Logger} is configured via static configuration instance, {@link LoggerConfig}, as
 * well as dynamic configuration options, {@link LoggerOptions}, which are specific to the
 * {@link Logger} instance being created.
 *
 * @param {LoggerConfig<D, U, A>} config
 *   The configuration instance, {@link LoggerConfig}, which represents configuration that is common
 *   to all {@link Logger} instances which are created in the same sub-project of this application.
 *
 *   The {@link LoggerConfig} instance should be instantiated once per sub-project (i.e. once for
 *   'nb-frontend', once for 'nb-react-native') and that instantiated instance, which represents
 *   static configuration specific to the sub-project, should be used to create all {@link Logger}
 *   instances in that sub-project.
 *
 * @param {LoggerOptions} options
 *   Optional options that are specific to the {@link Logger} instance that is being created.
 *
 *   @property {string} name
 *     The string name representation of the specific {@link Logger} instance.  This name will be
 *     included in traditional {@link pino} logs.
 *
 *   @property {U} user
 *     The user, {@link U}, that the {@link Logger} instance should be associated with on
 *     initialization. associated with.  If the user, {@link U}, is provided on initialization, the
 *     {@link Logger} will automatically perform the identification process w/ Amplitude on
 *     initialization of the {@link Logger}.  Otherwise, the identification process will be
 *     performed lazily, when the first call to the {@link Logger}'s {@link Logger.event} method is
 *     made when the {@link Logger} has been associated with a user, {@link U}.
 *
 *     Note: After initialization, the user, {@link U}, can be set on the {@link Logger} instance
 *     by setting the 'user' attribute.
 */
export class Logger<
  U extends AbstractUser,
  O extends LoggerOptions,
  A extends amplitude.AmplitudeConfig,
> {
  public name: string;
  private _config: LoggerConfig<readonly string[], U, A>;
  private _amplitudeInstance: amplitude.InferAmplitudeInstance<A> | null = null;
  private _lastAmplitudeIdentifiedUser: string | null = null;
  private _user: U | null = null;
  private _options?: O;

  constructor(
    config: LoggerConfig<readonly string[], U, A>,
    options?: O,
    staticOptions?: StaticLoggerOptions<U>,
  ) {
    this._config = config;
    this.name = config.defaultName;

    if (options) {
      this._options = options;
      this.name = staticOptions?.name || config.defaultName;
      this._user = staticOptions?.user || null;

      /* If the user is provided on initialization, set the user on the logger such that the user is
         identified with the amplitude instance. */
      if (staticOptions?.user) {
        this.user = staticOptions?.user;
      }
    }
  }

  public get config(): LoggerConfig<readonly string[], U, A> {
    return this._config;
  }

  public get options(): O {
    return this._options || ({} as O);
  }

  public set options(o: O) {
    this._options = o;
  }

  public get location(): LoggerLocation | undefined {
    /* The options may or may not include the location - in either case, it is safe to treat as
       undefined in the case that it is not on the options object. */
    return (this.options as { location: LoggerLocation }).location;
  }

  public get container(): amplitude.AmplitudeEventContainer | undefined {
    /* The options may or may not include the container - in either case, it is safe to treat as
       undefined in the case that it is not on the options object. */
    return (this.options as { container: amplitude.AmplitudeEventContainer })
      .container;
  }

  public get previousLocation(): LoggerLocation | undefined {
    return this.options.previousLocation;
  }

  private get amplitudeIdentifyCallback():
    | ((cls: amplitude.AmplitudeIdentify) => void)
    | undefined {
    return this.config.amplitude.identifyCallback;
  }

  public get event(): LogEvent<O> {
    return createEventLogger<U, O, A>(this);
  }

  public get user(): U {
    if (this._user === null) {
      throw new Error("The logger has not yet been assigned a user!");
    }
    return this._user;
  }

  public set user(user: U) {
    this._user = user;
  }

  public get amplitudeInstance(): amplitude.InferAmplitudeInstance<A> {
    if (!this._amplitudeInstance) {
      this._amplitudeInstance =
        this.config.amplitude.getInstance() as amplitude.InferAmplitudeInstance<A>;
      this._amplitudeInstance.init(
        this.config.amplitudeApiKey,
        /* Do not initialize the amplitude instance with a user - instead, allow the user to be set
           on the instance lazily when the user is associated with the logger. */
        undefined,
        this.config.amplitudeInitOptions,
      );
    }
    if (this._lastAmplitudeIdentifiedUser !== this.user.displayUserId) {
      const identifyClass = new this.config.amplitude.Identify()
        .set("Email", this.user.email)
        .set("Display user ID", this.user.displayUserId)
        .set(
          "Authed user ID",
          this.user.authedUserId ?? this.user.displayUserId,
        )
        .set("isAdmin", this.user.isAdmin);

      this.amplitudeIdentifyCallback?.(identifyClass);
      this._amplitudeInstance.identify(identifyClass);
      this._lastAmplitudeIdentifiedUser = this.user.displayUserId;
    }
    return this._amplitudeInstance;
  }

  public notify<C extends LoggerContext<U>>(
    error: Error | string,
    context?: C,
    options?: LogMethodOptions & {
      emitToBrowser?: boolean;
      __INTERNAL_DO_NOT_EMIT__?: boolean; // To prevent recursion
    },
  ) {
    if (this.config.transports.includes(LoggerTransports.BUGSNAG)) {
      throw new Error(
        "The BugSnag integration is not yet supported!  It cannot be included in the Logger's " +
          "transports.",
      );
    }
    /* Once implemented, this method will call the 'error' method with information provided in the
       arguments of this method. */
    const configEmitNotifications =
      options?.emitToBrowser === undefined
        ? this.config.emitNotificationsToBrowser
        : options.emitToBrowser;
    if (configEmitNotifications) {
      if (options?.__INTERNAL_DO_NOT_EMIT__ !== true) {
        this.error(error, context, options);
      }
    }
  }

  public internalRequestError() {
    throw new Error("This method is not yet implemented!");
  }

  private includeLocationInContext<C extends LoggerContext<U>>(
    curr: ParsedLoggerContext<U, C>,
    loc: LoggerLocation | LoggerParsedLocation | undefined,
    key: "previousLocation" | "location",
    user: U | null,
  ): ParsedLoggerContext<U, C> {
    if (loc) {
      if (user === null) {
        throw new Error(
          "When information about the location is provided, either the Logger instance must be " +
            "associated with a User, or the User must be included in the context.",
        );
      }
      return {
        ...curr,
        [key]: locationIsParsed(loc) ? loc : parseLocation(loc, user),
      };
    }
    return curr;
  }

  public parseContext<C extends LoggerContext<U>, OM extends LogMethodOptions>(
    context: C | undefined,
    options?: OM,
  ): ContextTransformerReturn<C, OM, U> {
    if (options?.autoInjectContext === false) {
      return (
        context ? prettifyContext(context) : null
      ) as ContextTransformerReturn<C, OM, U>;
    }

    const { location, previousLocation, user, ...rest } = context || {};

    const u: U | null = user || this._user;
    const loc = location || this.location;
    const previousLoc = previousLocation || this.previousLocation;

    const transformed: ParsedLoggerContext<U, C> =
      this.includeLocationInContext(
        this.includeLocationInContext(
          {
            ...rest,
            module: this.config.module,
            nodeEnvironment: this.config.nodeEnvironment,
            environment: this.config.environment,
          } as ParsedLoggerContext<U, C>,
          loc,
          "location",
          u,
        ),
        previousLoc,
        "previousLocation",
        u,
      );
    if (u !== null) {
      const t: ParsedLoggerContext<U, C> = {
        ...transformed,
        user: {
          id: u.authedUserId,
          isNorthbeamUser: u.email.endsWith("@northbeam.io"),
          name: `${u.firstName} ${u.lastName}`,
          email: u.email,
          timezone: u.timezone,
          impersonatedUserId: u.impersonatedUser?.id,
        },
      };
      return prettifyContext(t) as ContextTransformerReturn<C, OM, U>;
    }
    return prettifyContext(transformed) as ContextTransformerReturn<C, OM, U>;
  }

  private parseMethodArgs<
    C extends LoggerContext<U>,
    OM extends LogMethodOptions,
  >(
    arg0: string | C | Error,
    arg1?: C | string | OM,
    arg2?: C | OM,
    arg3?: OM,
  ): [ContextTransformerReturn<C, OM, U>, string | undefined, OM | undefined] {
    if (typeof arg0 === "string") {
      // Should be prevented externally by typeguards, but internally we still throw.
      if (typeof arg1 === "string") {
        throw new TypeError("Invalid implementation of function signature!");
      }
      /* Here, it is safe to assume that arg1 is the context and arg2 is the options, because the
         options must always be provided after the context. */
      return [
        this.parseContext(arg1 as C, arg2 as OM | undefined),
        arg0,
        arg2 as OM | undefined,
      ];
    } else if (arg0 instanceof Error) {
      const { message: errorMessage, name: errorName } = serializeError(arg0);
      if (typeof arg1 === "string") {
        return [
          {
            /* Here, it is safe to assume that arg2 is the context and arg3 is the options, because
               the options must always be provided after the context. */
            ...this.parseContext(arg2 as C, arg3 as OM | undefined),
            error: { errorName, errorMessage },
          },
          arg1,
          arg3,
        ];
      }
      return [
        {
          /* Here, it is safe to assume that arg1 is the context and arg2 is the options, because
             the options must always be provided after the context. */
          ...this.parseContext(arg1 as C, arg2 as OM | undefined),
          error: errorName,
        },
        errorMessage,
        arg2 as OM | undefined,
      ];
    }
    return [
      this.parseContext(arg0 as C, arg1 as OM | undefined),
      undefined,
      arg1 as OM | undefined,
    ];
  }

  // Use this form to issue a warning that does not have an Error or a message, but just context.
  public warn<C extends LoggerContext<U>>(
    context: C,
    opts?: ErrorMethodOptions<C, C, U>,
  ): void;
  // Use this form to issue a warning about an Error or a more general message.
  public warn<C extends LoggerContext<U>>(
    error: Error | string,
    context?: C,
    opts?: ErrorMethodOptions<Error | string, C, U>,
  ): void;
  /* Use this form to issue a warning about an Error but the log message should be different from
     the message that will be generated from the Error.  The message that is generated from the
     Error will still be included in the context, but the primary log message will be consistent
     with the provided 'msg' argument. */
  public warn<C extends LoggerContext<U>>(
    error: Error,
    msg: string,
    context?: C,
    opts?: ErrorMethodOptions<Error, C, U>,
  ): void;
  /**
   * Issues a log message, error and/or context with the 'warning' level.
   *
   * If the 'notify' option is provided, the method will additionally emit the error or message
   * string to BugSnag via the {@link Logger.notify} method.
   *
   * @param {Error | string | C} arg0
   *   Either an {@link Error} instance, a string warning message or a context object.  The
   *   following forms behave as follows:
   *
   *   1. {@link Error}
   *      When an {@link Error} instance is provided, the message associated with the {@link Error}
   *      instance will be used as the primary log message - unless the second argument is a
   *      message string - and information about the {@link Error} will be included in context.
   *
   *      If the 'notify' option is included, the {@link Error} will be emitted to BugSnag via the
   *      {@link Logger.notify} method.
   *
   *   2. {@link string}
   *      When a {@link string} message is provided, that message will be used as the primary log
   *      message.
   *
   *      If the 'notify' option is included, the {@link Error} will be emitted to BugSnag via the
   *      {@link Logger.notify} method.
   *
   *   3. {@link C}
   *      When the context, {@link C}, is provided, there will be no primary log message - but the
   *      log will just consist of the primary context.
   *
   *      The 'notify' option is not available.
   *
   * @param {C | string | ErrorMethodOptions<V, C, U>} arg1
   *   Either a context object, {@link C}, a message {@link string} or an options object,
   *   {@link ErrorMethodOptions} - depending on the form that the first argument came in.
   *
   * @param {C} arg2
   *    The context object, {@link C}, if not provided in the first set of arguments.  The options
   *    will always be the last argument.
   *
   * @param {ErrorMethodOptions} arg3
   *    The options, {@link ErrorMethodOptions}, if not provided in the first set of arguments.
   *
   * The relationship between the arguments above is better exemplified via examples of each
   * allowed usage:
   *
   * @example
   * // Issues a message-less warning log with the provided context.
   * logger.warn({ foo: "bar" }, {...options})
   *
   * @example
   * // Issues a warning log with the provided message and context.
   * logger.warn("This is a message.", { foo: "bar" }, {...options})
   *
   *  @example
   * // Issues a warning log with the message determined from the provided error, and context.
   * const err: Error;
   * logger.warn(err, { foo: "bar" }, {...options})
   *
   * @example
   * // Issues a warning log with the provided message and information from the provided error
   * // merged with the provided context.
   * const err: Error;
   * logger.warn(err, "This is a replacement message.", { foo: "bar" }, {...options})
   */
  public warn<V extends Error | string | C, C extends LoggerContext<U>>(
    arg0: V,
    arg1?: C | string | ErrorMethodOptions<V, C, U>,
    arg2?: C,
    arg3?: ErrorMethodOptions<V, C, U>,
  ) {
    const [ctx, msg, options] = this.parseMethodArgs<
      C,
      ErrorMethodOptions<V, C, U>
    >(arg0, arg1, arg2, arg3);
    if (ctx === null || Object.keys(ctx).length === 0) {
      this.config.pino.warn(msg);
    } else {
      this.config.pino.warn(ctx, msg);
    }
    if (options?.notify) {
      if (typeof arg0 === "string" || arg0 instanceof Error) {
        // Make sure to set the '__INTERNAL_DO_NOT_EMIT__' option to true to avoid a recursion.
        this.notify(arg0, ctx, { __INTERNAL_DO_NOT_EMIT__: true });
      } else {
        throw new TypeError(
          "Invalid signature implementation: The option 'notify' is only applicable if there " +
            "is a message or Error!",
        );
      }
    }
  }

  // Use this form to issue an error that does not have an Error or a message, but just context.
  public error<C extends LoggerContext<U>>(
    context: C,
    opts?: ErrorMethodOptions<C, C, U>,
  ): void;
  // Use this form to issue an error about an Error or a more general message.
  public error<C extends LoggerContext<U>>(
    error: Error | string,
    context?: C,
    opts?: ErrorMethodOptions<Error | string, C, U>,
  ): void;
  /* Use this form to issue an error about an Error but the log message should be different from
     the message that will be generated from the Error.  The message that is generated from the
     Error will still be included in the context, but the primary log message will be consistent
     with the provided 'msg' argument. */
  public error<C extends LoggerContext<U>>(
    error: Error,
    msg: string,
    context?: C,
    opts?: ErrorMethodOptions<Error, C, U>,
  ): void;
  /**
   * Issues a log message, error and/or context with the 'error' level.
   *
   * If the 'notify' option is provided, the method will additionally emit the error or message
   * string to BugSnag via the {@link Logger.notify} method.
   *
   * @param {Error | string | C} arg0
   *   Either an {@link Error} instance, a string warning message or a context object.  The
   *   following forms behave as follows:
   *
   *   1. {@link Error}
   *      When an {@link Error} instance is provided, the message associated with the {@link Error}
   *      instance will be used as the primary log message - unless the second argument is a
   *      message string - and information about the {@link Error} will be included in context.
   *
   *      If the 'notify' option is included, the {@link Error} will be emitted to BugSnag via the
   *      {@link Logger.notify} method.
   *
   *   2. {@link string}
   *      When a {@link string} message is provided, that message will be used as the primary log
   *      message.
   *
   *      If the 'notify' option is included, the {@link Error} will be emitted to BugSnag via the
   *      {@link Logger.notify} method.
   *
   *   3. {@link C}
   *      When the context, {@link C}, is provided, there will be no primary log message - but the
   *      log will just consist of the primary context.
   *
   *      The 'notify' option is not available.
   *
   * @param {C | string | ErrorMethodOptions<V, C, U>} arg1
   *   Either a context object, {@link C}, a message {@link string} or an options object,
   *   {@link ErrorMethodOptions} - depending on the form that the first argument came in.
   *
   * @param {C} arg2
   *    The context object, {@link C}, if not provided in the first set of arguments.  The options
   *    will always be the last argument.
   *
   * @param {ErrorMethodOptions} arg3
   *    The options, {@link ErrorMethodOptions}, if not provided in the first set of arguments.
   *
   * The relationship between the arguments above is better exemplified via examples of each
   * allowed usage:
   *
   * @example
   * // Issues a message-less error log with the provided context.
   * logger.error({ foo: "bar" }, {...options})
   *
   * @example
   * // Issues a error log with the provided message and context.
   * logger.error("This is a message.", { foo: "bar" }, {...options})
   *
   *  @example
   * // Issues a error log with the message determined from the provided error, and context.
   * const err: Error;
   * logger.error(err, { foo: "bar" }, {...options})
   *
   * @example
   * // Issues a error log with the provided message and information from the provided error
   * // merged with the provided context.
   * const err: Error;
   * logger.error(err, "This is a replacement message.", { foo: "bar" }, {...options})
   */
  public error<V extends Error | string | C, C extends LoggerContext<U>>(
    arg0: V,
    arg1?: C | string | ErrorMethodOptions<V, C, U>,
    arg2?: C,
    arg3?: ErrorMethodOptions<V, C, U>,
  ) {
    const [ctx, msg, options] = this.parseMethodArgs<
      C,
      ErrorMethodOptions<V, C, U>
    >(arg0, arg1, arg2, arg3);
    if (ctx === null || Object.keys(ctx).length === 0) {
      this.config.pino.error(msg);
    } else {
      this.config.pino.error(ctx, msg);
    }
    if (options?.notify) {
      if (typeof arg0 === "string" || arg0 instanceof Error) {
        // Make sure to set the '__INTERNAL_DO_NOT_EMIT__' option to false to avoid a recursion.
        this.notify(arg0, ctx, { __INTERNAL_DO_NOT_EMIT__: true });
      } else {
        throw new TypeError(
          "Invalid signature implementation: The option 'notify' is only applicable if there " +
            "is a message or Error!",
        );
      }
    }
  }

  public info<C extends LoggerContext<U>>(
    context: C,
    opts?: LogMethodOptions,
  ): void;
  public info<C extends LoggerContext<U>>(
    msg: string,
    context?: C,
    opts?: LogMethodOptions,
  ): void;
  /**
   * Issues a log message and/or context with the 'info' level.
   */
  public info<C extends LoggerContext<U>>(
    arg0: string | C,
    arg1?: C | LogMethodOptions,
    arg2?: LogMethodOptions,
  ) {
    const [ctx, msg] = this.parseMethodArgs(arg0, arg1, arg2);
    if (ctx === null || Object.keys(ctx).length === 0) {
      this.config.pino.info(msg);
    } else {
      this.config.pino.info(ctx, msg);
    }
  }

  /**
   * Issues a log message and/or context with the 'debug' level.
   */
  public debug<C extends LoggerContext<U>>(
    context: C,
    opts?: LogMethodOptions,
  ): void;
  public debug<C extends LoggerContext<U>>(
    msg: string,
    context?: C,
    opts?: LogMethodOptions,
  ): void;
  public debug<C extends LoggerContext<U>>(
    arg0: string | C,
    arg1?: C | LogMethodOptions,
    arg2?: LogMethodOptions,
  ) {
    const [ctx, msg] = this.parseMethodArgs(arg0, arg1, arg2);
    if (ctx === null || Object.keys(ctx).length === 0) {
      this.config.pino.debug(msg);
    } else {
      this.config.pino.debug(ctx, msg);
    }
  }

  /**
   * Issues a log message and/or context with the 'silent' level.
   */
  public silent<C extends LoggerContext<U>>(
    context: C,
    opts?: LogMethodOptions,
  ): void;
  public silent<C extends LoggerContext<U>>(
    msg: string,
    context?: C,
    opts?: LogMethodOptions,
  ): void;
  public silent<C extends LoggerContext<U>>(
    arg0: string | C,
    arg1?: C | LogMethodOptions,
    arg2?: LogMethodOptions,
  ) {
    const [ctx, msg] = this.parseMethodArgs(arg0, arg1, arg2);
    if (ctx === null || Object.keys(ctx).length === 0) {
      this.config.pino.silent(msg);
    } else {
      this.config.pino.silent(ctx, msg);
    }
  }
}
